import type { EditAudioSource, EditSource } from '@airpost/shared';
import { ALL_FORMATS, AudioBufferSink, BlobSource, Input, type InputAudioTrack } from 'mediabunny';

/**
 * What the timeline shows of the media: a filmstrip of the source and the waveforms of its sound, of the
 * voice-over and of the music, drawn once as images. The frames come from a `<video>` element seeking through
 * the file (event-driven, so the page stays responsive), the sound from `decodeAudioData`, with Mediabunny's
 * audio sink as the fallback for a container the browser's audio decoder does not read.
 */
export interface Thumb {
  tS: number;
  url: string;
}
export interface SourceAnalysis {
  durationS: number;
  thumbs: Thumb[];
  wave: string | null;
}
export interface AnalyseOptions {
  thumbs?: number;
  width?: number;
  height?: number;
  buckets?: number;
}

/** Where a piece's file is read from: the playout's proxy for a library clip, the story's own copy for an import or a photo. */
export const sourceUrlOf = (s: EditSource): string => (s.kind === 'clip' ? `/api/media/${encodeURIComponent(s.id)}/proxy` : `/api/uploads/${encodeURIComponent(s.id)}/file`);
/** Where an audio clip's file is read from: the story's take, the station's bed, or (a piece's separated sound) the piece's own file. */
export const audioUrlOf = (s: EditAudioSource, storyId: string): string =>
  s.kind === 'voiceover' ? `/api/stories/${storyId}/voiceovers/${encodeURIComponent(s.id)}/audio` : s.kind === 'music' ? `/api/music/${encodeURIComponent(s.id)}/audio` : sourceUrlOf({ kind: s.kind, id: s.id });

const files = new Map<string, Promise<Blob>>();
/**
 * The source file, fetched once and kept for the page's life: the proxy answers each small ranged read slowly
 * (it streams from the playout), so one whole read and then local reads serve the filmstrip and the export.
 */
export function sourceBlob(url: string): Promise<Blob> {
  let p = files.get(url);
  if (!p) {
    p = fetch(url, { credentials: 'same-origin' }).then((r) => {
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.blob();
    });
    p.catch(() => files.delete(url));
    files.set(url, p);
  }
  return p;
}
const stills = new Map<string, Promise<{ img: CanvasImageSource; w: number; h: number }>>();
/** A photo, decoded once for the page's life (the monitor and the export share it). */
export function loadStill(url: string): Promise<{ img: CanvasImageSource; w: number; h: number }> {
  let p = stills.get(url);
  if (!p) {
    p = sourceBlob(url).then(async (blob) => {
      const bmp = await createImageBitmap(blob);
      return { img: bmp, w: bmp.width, h: bmp.height };
    });
    p.catch(() => stills.delete(url));
    stills.set(url, p);
  }
  return p;
}
/** The source opened for reading with Mediabunny (dispose it when done). */
export const openSource = async (url: string): Promise<Input> => new Input({ formats: ALL_FORMATS, source: new BlobSource(await sourceBlob(url)) });

/** A turn of the event loop, so timers, input and painting get through between heavy steps. */
export const breathe = (): Promise<void> => new Promise((resolve) => window.setTimeout(resolve, 0));

const once = (el: EventTarget, event: string, ms = 20_000): Promise<void> =>
  new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => {
      el.removeEventListener(event, ok);
      el.removeEventListener('error', bad);
      reject(new Error(`${event}: timeout`));
    }, ms);
    const ok = () => {
      window.clearTimeout(timer);
      el.removeEventListener('error', bad);
      resolve();
    };
    const bad = () => {
      window.clearTimeout(timer);
      el.removeEventListener(event, ok);
      reject(new Error(`${event}: error`));
    };
    el.addEventListener(event, ok, { once: true });
    el.addEventListener('error', bad, { once: true });
  });

/** The loudest sample of each bucket, 0..1 (every fourth sample is enough for a picture). */
function bucketPeaks(into: Float32Array, data: Float32Array, sampleRate: number, offsetS: number, totalS: number): void {
  const n = into.length;
  for (let i = 0; i < data.length; i += 4) {
    const b = Math.min(n - 1, Math.max(0, Math.floor(((offsetS + i / sampleRate) / totalS) * n)));
    const v = Math.abs(data[i]!);
    if (v > into[b]!) into[b] = v;
  }
}
const normalised = (peaks: Float32Array): number[] => {
  let max = 0;
  for (const v of peaks) if (v > max) max = v;
  return Array.from(peaks, (v) => (max > 0 ? v / max : 0));
};

async function trackPeaks(track: InputAudioTrack, durationS: number, buckets: number): Promise<number[]> {
  const peaks = new Float32Array(buckets);
  const sink = new AudioBufferSink(track);
  let n = 0;
  for await (const { buffer, timestamp } of sink.buffers()) {
    bucketPeaks(peaks, buffer.getChannelData(0), buffer.sampleRate, timestamp, Math.max(0.001, durationS));
    if (++n % 20 === 0) await breathe();
  }
  return normalised(peaks);
}

/** A waveform as an image, one pixel per bucket, to stretch over a clip on the timeline. */
export function waveformUrl(peaks: number[], color: string, height = 48): string {
  const c = document.createElement('canvas');
  c.width = Math.max(1, peaks.length);
  c.height = height;
  const ctx = c.getContext('2d')!;
  ctx.fillStyle = color;
  const mid = height / 2;
  peaks.forEach((p, i) => {
    const a = Math.max(0.5, p * mid * 0.94);
    ctx.fillRect(i, mid - a, 1, a * 2);
  });
  return c.toDataURL('image/png');
}

/** The sound of a file: the browser's decoder first, Mediabunny's for a container it does not read. */
async function filePeaks(blob: Blob, durationS: number, buckets: number): Promise<number[] | null> {
  try {
    const buffer = await new OfflineAudioContext(1, 1, 48_000).decodeAudioData(await blob.arrayBuffer());
    const peaks = new Float32Array(buckets);
    bucketPeaks(peaks, buffer.getChannelData(0), buffer.sampleRate, 0, Math.max(0.001, durationS || buffer.duration));
    return normalised(peaks);
  } catch {
    const input = new Input({ formats: ALL_FORMATS, source: new BlobSource(blob) });
    try {
      const audio = await input.getPrimaryAudioTrack();
      if (!audio || !(await audio.canDecode())) return null;
      return await trackPeaks(audio, durationS || (await audio.computeDuration()), buckets);
    } finally {
      input.dispose();
    }
  }
}

async function analyse(url: string, opts: AnalyseOptions): Promise<SourceAnalysis> {
  const blob = await sourceBlob(url);
  const objectUrl = URL.createObjectURL(blob);
  const video = document.createElement('video');
  video.muted = true;
  video.preload = 'auto';
  video.playsInline = true;
  try {
    video.src = objectUrl;
    await once(video, 'loadedmetadata');
    const durationS = Number.isFinite(video.duration) ? video.duration : 0;
    const thumbs: Thumb[] = [];
    const wanted = opts.thumbs ?? 32;
    if (wanted > 0 && durationS > 0 && video.videoWidth > 0) {
      const w = opts.width ?? 128;
      const h = opts.height ?? 72;
      const c = document.createElement('canvas');
      c.width = w;
      c.height = h;
      const ctx = c.getContext('2d')!;
      // cover: crop the frame to the thumbnail's shape
      const scale = Math.max(w / video.videoWidth, h / video.videoHeight);
      const dw = video.videoWidth * scale;
      const dh = video.videoHeight * scale;
      const n = Math.max(1, Math.min(wanted, Math.ceil(durationS)));
      for (let i = 0; i < n; i++) {
        const tS = ((i + 0.5) * durationS) / n;
        video.currentTime = tS;
        try {
          await once(video, 'seeked');
        } catch {
          continue; // a seek the browser never answered: the strip has a gap there, the rest goes on
        }
        ctx.drawImage(video, (w - dw) / 2, (h - dh) / 2, dw, dh);
        thumbs.push({ tS, url: c.toDataURL('image/jpeg', 0.72) });
      }
    }
    let wave: string | null = null;
    if ((opts.buckets ?? 800) > 0) {
      const peaks = await filePeaks(blob, durationS, opts.buckets ?? 800);
      if (peaks) wave = waveformUrl(peaks, 'rgba(255,255,255,0.5)');
    }
    return { durationS, thumbs, wave };
  } finally {
    video.removeAttribute('src');
    video.load();
    URL.revokeObjectURL(objectUrl);
  }
}

const analyses = new Map<string, Promise<SourceAnalysis>>();
/** The source clip: its frames every so often and its sound, from the same-origin proxy; done once per clip. */
export function analyseSource(url: string, opts: AnalyseOptions = {}): Promise<SourceAnalysis> {
  const key = `${url}|${opts.thumbs ?? ''}|${opts.buckets ?? ''}`;
  let p = analyses.get(key);
  if (!p) {
    p = analyse(url, opts);
    p.catch(() => analyses.delete(key));
    analyses.set(key, p);
  }
  return p;
}

/** A sound file (voice-over or music): its exact length and its waveform. */
export async function analyseSound(url: string, color: string, buckets = 600): Promise<{ durationMs: number; wave: string }> {
  const r = await fetch(url, { credentials: 'same-origin' });
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  const buffer = await new OfflineAudioContext(1, 1, 48_000).decodeAudioData(await r.arrayBuffer());
  const peaks = new Float32Array(buckets);
  bucketPeaks(peaks, buffer.getChannelData(0), buffer.sampleRate, 0, Math.max(0.001, buffer.duration));
  return { durationMs: Math.round(buffer.duration * 1000), wave: waveformUrl(normalised(peaks), color) };
}

/** A sound whose file is longer than this is not decoded whole (the monitor then plays it from the video element). */
export const MAX_DECODE_S = 900;
const decoded = new Map<string, Promise<AudioBuffer | null>>();

/** Mediabunny's audio sink into one buffer, for a container the browser's decoder does not read. */
async function assemble(blob: Blob, maxS: number): Promise<AudioBuffer | null> {
  const input = new Input({ formats: ALL_FORMATS, source: new BlobSource(blob) });
  try {
    const track = await input.getPrimaryAudioTrack();
    if (!track || !(await track.canDecode())) return null;
    const durS = await track.computeDuration();
    if (!durS || durS > maxS) return null;
    let out: AudioBuffer | null = null;
    let n = 0;
    for await (const { buffer, timestamp } of new AudioBufferSink(track).buffers()) {
      out ??= new AudioBuffer({ numberOfChannels: buffer.numberOfChannels, length: Math.ceil((durS + 1) * buffer.sampleRate), sampleRate: buffer.sampleRate });
      const at = Math.max(0, Math.round(timestamp * buffer.sampleRate));
      for (let ch = 0; ch < Math.min(out.numberOfChannels, buffer.numberOfChannels); ch++) {
        const data = buffer.getChannelData(ch);
        out.copyToChannel(data.subarray(0, Math.max(0, Math.min(data.length, out.length - at))), ch, at);
      }
      if (++n % 20 === 0) await breathe();
    }
    return out;
  } finally {
    input.dispose();
  }
}

/**
 * A file's sound as one buffer, decoded once per page: the browser's decoder first, Mediabunny's as the fallback;
 * null when the file has no sound, cannot be read, or is longer than MAX_DECODE_S.
 */
export function decodeSound(url: string, opts: { whole?: boolean } = {}): Promise<AudioBuffer | null> {
  let p = decoded.get(url);
  if (!p) {
    p = (async () => {
      const blob = opts.whole === false ? await fetch(url, { credentials: 'same-origin' }).then((r) => (r.ok ? r.blob() : Promise.reject(new Error(`HTTP ${r.status}`)))) : await sourceBlob(url);
      try {
        const buf = await new OfflineAudioContext(1, 1, 48_000).decodeAudioData(await blob.arrayBuffer());
        return buf.duration > MAX_DECODE_S ? null : buf;
      } catch {
        try {
          return await assemble(blob, MAX_DECODE_S);
        } catch {
          return null;
        }
      }
    })();
    p.catch(() => decoded.delete(url));
    decoded.set(url, p);
  }
  return p;
}

/** The loudest sample of a sound (0..1), for "normalise". */
export function peakOf(buffer: AudioBuffer, fromS = 0, toS = buffer.duration): number {
  let peak = 0;
  const a = Math.max(0, Math.floor(fromS * buffer.sampleRate));
  const b = Math.min(buffer.length, Math.ceil(toS * buffer.sampleRate));
  for (let ch = 0; ch < buffer.numberOfChannels; ch++) {
    const d = buffer.getChannelData(ch);
    for (let i = a; i < b; i++) {
      const v = Math.abs(d[i]!);
      if (v > peak) peak = v;
    }
  }
  return peak;
}

/** Frames of a source at the given seconds, as small JPEGs (base64, no prefix): what the AI editor looks at. */
export async function captureFrames(url: string, timesS: number[], w = 320, h = 180): Promise<Array<{ tMs: number; jpeg: string }>> {
  const blob = await sourceBlob(url);
  const objectUrl = URL.createObjectURL(blob);
  const video = document.createElement('video');
  video.muted = true;
  video.preload = 'auto';
  video.playsInline = true;
  try {
    video.src = objectUrl;
    await once(video, 'loadedmetadata');
    if (!video.videoWidth) return [];
    const c = document.createElement('canvas');
    c.width = w;
    c.height = h;
    const ctx = c.getContext('2d')!;
    const scale = Math.min(w / video.videoWidth, h / video.videoHeight);
    const dw = video.videoWidth * scale;
    const dh = video.videoHeight * scale;
    const out: Array<{ tMs: number; jpeg: string }> = [];
    for (const tS of timesS) {
      video.currentTime = Math.min(Math.max(0, tS), Math.max(0, (video.duration || tS) - 0.05));
      try {
        await once(video, 'seeked');
      } catch {
        continue;
      }
      ctx.fillStyle = '#000';
      ctx.fillRect(0, 0, w, h);
      ctx.drawImage(video, (w - dw) / 2, (h - dh) / 2, dw, dh);
      out.push({ tMs: Math.round(tS * 1000), jpeg: c.toDataURL('image/jpeg', 0.7).split(',')[1] ?? '' });
    }
    return out;
  } finally {
    video.removeAttribute('src');
    video.load();
    URL.revokeObjectURL(objectUrl);
  }
}
